import { Component, OnInit, Input, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { IconNames } from '@shared/enums/icon-names';
import { MobilePhoneFormControls } from '@sales/modules/mobilephone/components/mobilephone-search/mobilephone-details/mobilephone-details.component';
import { HardwareInsuranceOption } from '@sales/modules/mobilephone/models/mobilephone-search-item';
import { MobilePaymentType } from '@sales/enums/mobile-payment-type';
import { VoiceProduct } from '@app/shared/models/voice-product.model';
import { HardwareVariant } from '@app/sales/modules/mobilephone/models/mobilephone-variant';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { IceBadgeComponent } from '@app/shared/components/atoms/ice-badge/ice-badge.component';
import { MatIcon } from '@angular/material/icon';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { DecimalPipe } from '@angular/common';

export enum InsuranceOptionNames {
  Plussforsikring = 'Plussforsikring',
  Skadeforsikring = 'Skadeforsikring',
  NoInsurance = 'NoInsurance',
}

@Component({
  selector: 'app-mobilephone-insurance-options',
  templateUrl: './mobilephone-insurance-options.component.html',
  styleUrls: ['./mobilephone-insurance-options.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [IceAlertComponent, IceSelectBoxComponent, MatIcon, IceBadgeComponent, PriceFormatPipe, DecimalPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MobilephoneInsuranceOptionsComponent implements OnInit, OnDestroy {
  formGroupDirective = inject(FormGroupDirective);

  MobilePhoneFormControls = MobilePhoneFormControls;
  InsuranceOptionNames = InsuranceOptionNames;
  MobilePaymentType = MobilePaymentType;
  PriceType = PriceType;
  IconNames = IconNames;
  form: FormGroup;
  selectedPaymentType: MobilePaymentType;
  subscriptions$: Subscription[] = [];

  @Input() mobilePhoneVariant: HardwareVariant;
  @Input() allVoiceProducts: VoiceProduct[];
  @Input() mobileProductId: string;

  mobileInsuranceNotAllowed: boolean;
  isMobilBytteSelected = false;
  hasChangedMobilBytteOffering = false;
  get gtinControl() {
    return this.form.get(MobilePhoneFormControls.Gtin);
  }

  get insuranceOptionControl() {
    return this.form.get(MobilePhoneFormControls.InsuranceOption);
  }

  get paymentTypeControl() {
    return this.form.get(MobilePhoneFormControls.PaymentType);
  }

  ngOnInit() {
    this.form = this.formGroupDirective.form;

    this.mobileInsuranceNotAllowed = this.isMobileInsuranceNotAllowed();
    this.isMobilBytteSelected = !!this.form.get(MobilePhoneFormControls.MobilBytteOffering).value;

    this.subscriptions$.push(
      this.form.valueChanges.subscribe(() => {
        this.mobileInsuranceNotAllowed = this.isMobileInsuranceNotAllowed();

        if (this.isMobilBytteSelected && !this.mobileProductId) {
          if (!this.insuranceOptionControl.value) {
            this.insuranceOptionControl.setErrors({
              required: true,
            });
          }
        } else if (this.isMobileProductWithNoInsurance) {
          this.form.get(MobilePhoneFormControls.InsuranceOption).setValue(InsuranceOptionNames.NoInsurance);
        } else if (
          this.insuranceOptionControl.value !== InsuranceOptionNames.NoInsurance &&
          this.mobileInsuranceNotAllowed &&
          this.isInsurancesAvailable
        ) {
          this.insuranceOptionControl.setErrors({
            insuranceRequireSubscription: true,
          });
        }
      }),
      this.form.get(MobilePhoneFormControls.MobilBytteOffering).valueChanges.subscribe((value: string) => {
        this.isMobilBytteSelected = !!value;
        this.hasChangedMobilBytteOffering = true;
        if (this.isInsurancesAvailable) {
          this.form.get(MobilePhoneFormControls.InsuranceOption).setValue(null);
        }
      }),
      this.paymentTypeControl.valueChanges.subscribe((paymentType: MobilePaymentType) => {
        this.selectedPaymentType = paymentType;
        this.isMobilBytteSelected = paymentType === MobilePaymentType.EquipmentUpgrade;
      })
    );
  }

  get alertType(): 'info' | 'error' {
    if (this.insuranceOptionControl.value !== null) {
      if (this.mobileInsuranceNotAllowed) {
        if (this.insuranceOptionControl.value === InsuranceOptionNames.NoInsurance) {
          return 'info';
        } else {
          return 'error';
        }
      }
    }
  }
  get insuranceOptions(): HardwareInsuranceOption[] {
    if (this.isMobilBytteSelected) {
      const mobilBytteOfferingId = this.form.get(MobilePhoneFormControls.MobilBytteOffering).value;
      const mobilBytteInsuranceOptions = this.mobilePhoneVariant?.mobilbytteInsuranceOptions.find(
        x => x.mobilbytteOfferingId === mobilBytteOfferingId
      );
      return mobilBytteInsuranceOptions?.insuranceOptions || [];
    }

    return this.mobilePhoneVariant?.insuranceOptions;
  }

  isMobileInsuranceNotAllowed() {
    let activeVoiceProducts = this.allVoiceProducts;
    if (this.mobileProductId) {
      activeVoiceProducts = this.allVoiceProducts.filter(x => x.mobileProductId !== this.mobileProductId);
    }

    return activeVoiceProducts.length === 0 && this.paymentTypeControl.value === MobilePaymentType.PayNowWithoutSubscription;
  }

  get isMobileProductWithNoInsurance(): boolean {
    return (
      this.mobileProductId &&
      !this.insuranceOptionControl.value &&
      this.isInsurancesAvailable &&
      !this.hasChangedMobilBytteOffering
    );
  }

  get isInsurancesAvailable(): boolean {
    return this.insuranceOptions?.length > 0;
  }

  isDiscounted(option: HardwareInsuranceOption): boolean {
    return (
      option.priceInformation.isDiscounted &&
      option.priceInformation.discountLength > 0 &&
      option.priceInformation.discountPercentage > 0
    );
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
